import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { STUDIO } from "@/data/content";

const links = [
  { id: "about", href: "#about" },
  { id: "services", href: "#services" },
  { id: "portfolio", href: "#portfolio" },
  { id: "vision", href: "#vision" },
  { id: "contact", href: "#contact" },
];

export const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isHome = pathname === "/";
  const getHref = (hash) => (isHome ? hash : `/${hash}`);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "backdrop-blur-2xl bg-black/50 border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to={getHref("#top")}
          data-testid="nav-logo"
          className="flex items-center gap-2 group"
        >
          <span className="text-[25.2px] font-heading font-semibold tracking-tight text-white">
            Inovo<span className="text-[#00F0FF]">Design</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <Link
              key={l.id}
              to={getHref(l.href)}
              data-testid={`nav-link-${l.id}`}
              className="text-sm text-zinc-400 hover:text-white transition-colors duration-300"
            >
              {t.nav[l.id]}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-switcher"
            className="font-mono text-xs tracking-widest text-zinc-300 border border-white/10 rounded-md px-3 py-2 hover:border-[#00F0FF]/40 hover:text-[#00F0FF] transition-colors duration-300"
          >
            {lang === "it" ? "IT / EN" : "EN / IT"}
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white text-sm border border-white/10 rounded-md px-3 py-2"
          >
            {open ? "✕" : "☰"}
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden bg-black/90 border-t border-white/5 px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <Link
              key={l.id}
              to={getHref(l.href)}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${l.id}`}
              className="text-zinc-300 hover:text-white text-base"
            >
              {t.nav[l.id]}
            </Link>
          ))}
          <a
            href={`mailto:${STUDIO.email}`}
            data-testid="mobile-contact-email-link"
            className="font-mono text-xs text-[#00F0FF]"
          >
            {STUDIO.email}
          </a>
        </div>
      )}
    </motion.header>
  );
};
